<template>
  <DiyEditor
    v-model="pageConfig"
    title="酒店移动端 DIY"
    :libs="PAGE_LIBS"
    :show-tab-bar="true"
    @save="saveTemplate"
  >
    <template #toolBarLeft>
      <div class="mobile-toolbar-brand">
        <span>Hotel Mobile</span>
        <strong>DIY</strong>
      </div>
      <el-button-group>
        <el-tooltip content="返回">
          <el-button @click="router.back()">
            <Icon :size="22" icon="ep:back" />
          </el-button>
        </el-tooltip>
        <el-tooltip content="生成模板">
          <el-button @click="generateTemplate">
            <Icon :size="22" icon="ep:document-copy" />
          </el-button>
        </el-tooltip>
      </el-button-group>
    </template>
  </DiyEditor>

  <Dialog v-model="templateDialogVisible" title="酒店移动端模板配置" width="760px">
    <el-input :model-value="templateJson" type="textarea" :rows="18" readonly />
  </Dialog>
</template>

<script setup lang="ts">
import { cloneDeep } from 'lodash-es'
import { useRouter } from 'vue-router'
import { PAGE_LIBS, type PageConfig } from '@/components/DiyEditor/util'

defineOptions({ name: 'HotelMobileDesign' })

const router = useRouter()
const message = useMessage()
const storageKey = 'hotel-mobile-diy-template'

const defaultTemplate: PageConfig = {
  page: {
    description: '酒店移动端首页',
    backgroundColor: '#F8FAFC',
    backgroundImage: ''
  } as any,
  navigationBar: {
    bgType: 'color',
    bgColor: '#EEF6FF',
    bgImg: '',
    styleType: 'normal',
    alwaysShow: true,
    mpCells: [{ type: 'text', text: '酒店预订', textColor: '#142033', width: 120, height: 32, top: 0, left: 0 }],
    otherCells: [{ type: 'text', text: '酒店预订', textColor: '#142033', width: 120, height: 32, top: 0, left: 0 }],
    _local: { previewMp: false, previewOther: true }
  } as any,
  tabBar: {
    theme: 'white',
    style: {},
    items: [
      { text: '首页', iconUrl: '', activeIconUrl: '', url: '/pages/modules/hotel/index/index' },
      { text: '订单', iconUrl: '', activeIconUrl: '', url: '/pages/modules/hotel/booking/my-orders' },
      { text: '会员', iconUrl: '', activeIconUrl: '', url: '/pages/modules/hotel/member/index' }
    ]
  } as any,
  components: [
    {
      id: 'SearchBar',
      property: {
        height: 34,
        showScan: false,
        borderRadius: 18,
        placeholder: '搜索酒店、商圈、地标',
        placeholderPosition: 'left',
        backgroundColor: '#FFFFFF',
        textColor: '#8A93A3',
        hotKeywords: ['钟点房', '亲子房', '商务优选'],
        style: { bgType: 'color', bgColor: '#EEF6FF', paddingTop: 12, paddingRight: 12, paddingBottom: 12, paddingLeft: 12, marginBottom: 8 }
      }
    },
    {
      id: 'Carousel',
      property: {
        type: 'default',
        indicator: 'dot',
        autoplay: true,
        interval: 3,
        height: 170,
        items: [
          { type: 'img', imgUrl: 'https://picsum.photos/seed/hotel-mobile-banner-a/750/340', videoUrl: '', url: '' },
          { type: 'img', imgUrl: 'https://picsum.photos/seed/hotel-mobile-banner-b/750/340', videoUrl: '', url: '' }
        ],
        style: { bgType: 'color', bgColor: '#F8FAFC', marginLeft: 12, marginRight: 12, marginBottom: 8, borderRadius: 12 }
      }
    },
    {
      id: 'MenuGrid',
      property: {
        column: 5,
        list: [
          { title: '钟点房', titleColor: '#142033', subtitle: '', subtitleColor: '#8A93A3', iconUrl: '', url: '/pages/modules/hotel/pages/hourly/index', badge: { show: false, text: '', textColor: '#fff', bgColor: '#FF6000' } },
          { title: '商务优选', titleColor: '#142033', subtitle: '', subtitleColor: '#8A93A3', iconUrl: '', url: '/pages/modules/hotel/detail/index', badge: { show: false, text: '', textColor: '#fff', bgColor: '#FF6000' } },
          { title: '家庭出游', titleColor: '#142033', subtitle: '', subtitleColor: '#8A93A3', iconUrl: '', url: '/pages/modules/hotel/pages/destination', badge: { show: false, text: '', textColor: '#fff', bgColor: '#FF6000' } },
          { title: '续住', titleColor: '#142033', subtitle: '', subtitleColor: '#8A93A3', iconUrl: '', url: '/pages/modules/hotel/booking/my-orders?tab=2', badge: { show: false, text: '', textColor: '#fff', bgColor: '#FF6000' } },
          { title: '消息', titleColor: '#142033', subtitle: '', subtitleColor: '#8A93A3', iconUrl: '', url: '/pages/modules/hotel/pages/notification/index', badge: { show: true, text: '新', textColor: '#fff', bgColor: '#2F80ED' } }
        ],
        style: { bgType: 'color', bgColor: '#FFFFFF', marginLeft: 12, marginRight: 12, marginBottom: 8, padding: 10, borderRadius: 12 }
      }
    },
    {
      id: 'TitleBar',
      property: {
        title: '精选酒店',
        description: '好评与热卖房型推荐',
        titleSize: 17,
        descriptionSize: 12,
        titleWeight: 700,
        descriptionWeight: 400,
        textAlign: 'left',
        titleColor: '#142033',
        descriptionColor: '#8A93A3',
        marginLeft: 0,
        height: 48,
        more: { show: true, type: 'text', text: '更多', url: '/pages/modules/hotel/index/index' },
        style: { bgType: 'color', bgColor: '#F8FAFC', paddingLeft: 12, paddingRight: 12 }
      } as any
    },
    {
      id: 'ProductList',
      property: {
        layoutType: 'twoCol',
        fields: { name: { show: true, color: '#142033' }, price: { show: true, color: '#D64545' } },
        badge: { show: false, imgUrl: '' },
        borderRadiusTop: 10,
        borderRadiusBottom: 10,
        space: 8,
        spuIds: [],
        style: { bgType: 'color', bgColor: '#F8FAFC', marginLeft: 12, marginRight: 12, marginBottom: 12 }
      }
    }
  ]
}

const pageConfig = ref<PageConfig>(cloneDeep(defaultTemplate))
const templateDialogVisible = ref(false)
const templateJson = ref('')

const loadTemplate = () => {
  const saved = localStorage.getItem(storageKey)
  pageConfig.value = saved ? JSON.parse(saved) : cloneDeep(defaultTemplate)
}

const saveTemplate = () => {
  localStorage.setItem(storageKey, JSON.stringify(pageConfig.value))
  message.success('保存成功')
}

const generateTemplate = () => {
  templateJson.value = JSON.stringify(
    {
      type: 'hotel-mobile',
      version: '1.0.0',
      page: pageConfig.value
    },
    null,
    2
  )
  templateDialogVisible.value = true
}

onMounted(loadTemplate)
</script>

<style scoped lang="scss">
.mobile-toolbar-brand {
  height: 100%;
  padding: 0 16px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-right: 1px solid #e8ecf2;
  color: #202532;
}

.mobile-toolbar-brand span {
  font-size: 13px;
  font-weight: 700;
}

.mobile-toolbar-brand strong {
  height: 22px;
  padding: 0 8px;
  border-radius: 99px;
  display: inline-flex;
  align-items: center;
  background: #eafaf5;
  color: #00a67d;
  font-size: 12px;
}

:deep(.editor) {
  background: #f5f7fb;
}

:deep(.editor-header) {
  border: 1px solid #e8ecf2;
  border-radius: 8px 8px 0 0;
  background: #fff;
}

:deep(.header-center span) {
  font-weight: 700;
  color: #202532;
}

:deep(.editor-container) {
  background:
    linear-gradient(90deg, rgba(47, 128, 237, 0.04), rgba(0, 166, 125, 0.04)),
    #f5f7fb;
}

:deep(.editor-center) {
  padding-top: 18px;
}

:deep(.editor-design-top),
:deep(.editor-design-center),
:deep(.editor-design-bottom) {
  box-shadow: 0 12px 28px rgba(31, 42, 61, 0.08);
}

:deep(.component-library) {
  border-right: 1px solid #e8ecf2;
}

:deep(.editor-right) {
  background: #fff;
}
</style>
